import Logo from '@App/session/components/Logo'
import Button from '@Components/Button'
import Modal from '@Components/Modal'
import { isPresent } from '@Helpers/functional'
import { hubAtom, restApiAtom } from '@Modules/hubs/atoms'
import FooterLinks from '@Modules/hubs/common/components/FooterLinks'
import ErrorPage from '@Pages/_error'
import { HubResponse } from '@RestApi/client'
import React, { useState } from 'react'
import { useRecoilValue } from 'recoil'
import styled from 'styled-components'
import validator from 'validator'
import LoginModal from './LoginModal'
import Placeholder from './Placeholder'
import RequestResetPassword from './RequestResetPassword'

type props = {}

const HubSignIn: React.FC<props> = (props: props) => {
  const restApi = useRecoilValue<any>(restApiAtom)
  const hubResponse = useRecoilValue<HubResponse>(hubAtom)

  const [emailAddress, setEmailAddress] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [action, setAction] = useState<string>('ask-email')

  const [badEmailError, setBadEmailError] = useState<boolean>(false)
  const [emptyEmailError, setEmptyEmailError] = useState<boolean>(false)
  const [isAuthorized, setIsAuthorized] = useState<boolean>(true)

  const [loading, setLoading] = useState<boolean>(false)

  const [isRequestResetPassword, setIsRequestResetPassword] = useState<Boolean>(false)

  // if (hubResponse?.error && hubResponse.error !== 'HTTP 401') {
  //   return (
  //     <ErrorPage
  //       msg={`Please sign-in.`}
  //       error={hubResponse.error}
  //       vctraceid={hubResponse.vctraceid}
  //     />
  //   )
  // }

  if (!hubResponse?.data) {
    return (
      <ErrorPage
        msg={`Empty hubResponse.data`}
        error={hubResponse?.error}
        vctraceid={hubResponse?.vctraceid}
      />
    )
  }

  const hub = hubResponse.data

  const tintStyles = {
    backgroundColor: hub.theme.coverTintColorWithOpacity,
  }

  const handleSetEmailAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailAddress(e.target.value)
  }

  const resetAction = () => {
    setAction('ask-email')
    setPassword('')
    setIsAuthorized(true)
    setBadEmailError(false)
    setEmptyEmailError(false)
  }

  const validateEmailAddress = (): boolean => {
    if (emailAddress === '') {
      setEmptyEmailError(true)
      setBadEmailError(false)
      return false
    } else if (!validator.isEmail(emailAddress)) {
      setBadEmailError(true)
      setEmptyEmailError(false)
      return false
    } else return true
  }

  const handleEmailSubmit = async () => {
    if (validateEmailAddress()) {
      setLoading(true)
      const resp = await restApi.fetchHubSignInWorkflow(emailAddress, hub.id)
      setLoading(false)
      setAction(resp.action)
    }
  }

  const handlePasswordSubmit = (password: string) => {
    setPassword(password)
  }

  const handleSignIn = async () => {
    setLoading(true)
    const res = await restApi.signIn(emailAddress, password, '/', hub.id)
    setLoading(false)

    if (res.error && res.error === 'unauthorized') {
      setIsAuthorized(false)
      return
    }
    setIsAuthorized(true)
    setAction('auth-success')
  }

  const handleRequesResetPassword = async () => {
    setLoading(true)
    const resp = await restApi.requestResetPassword(emailAddress, hub)
    setLoading(false)

    if (resp.status === 204) {
      // need to be revised
      setAction('requestResetPassword-success')
    }
  }

  const hideRequestResetPassword = () => {
    setAction('ask-email')
    setEmailAddress('')
    setBadEmailError(false)
    setEmptyEmailError(false)
    setIsRequestResetPassword(false)
  }

  const renderLogo = () => {
    const loginLogoURL: string | undefined = hub.settings.login_logo_url
    return <Logo hub={hub} org={hub.account} loginLogoURL={loginLogoURL} signInContext={'hub'} />
  }

  const showResetPassword = () => {
    setIsRequestResetPassword(true)
  }

  const logoUrl = hub.logo_url ? hub.logo_url : ''

  const backgroundUrl = isPresent(hub.cover_url)
    ? hub.cover_url
    : hub.account && isPresent(hub.account.cover_url)
    ? hub.account.cover_url
    : ''
  return (
    <Container>
      <ShadowOverlay />
      <h1 className="hidden-elements">{hub.name}</h1>
      <ModalContainer className="px-login-container">
        <Modal overlay renderLogo={renderLogo}>
          {isRequestResetPassword ? (
            <RequestResetPassword
              hub={hub}
              onCloseResetPassword={hideRequestResetPassword}
              emailAddress={emailAddress}
              badEmailError={badEmailError}
              emptyEmailError={emptyEmailError}
              loading={loading}
              action={action}
              resetAction={resetAction}
              isAuthorized={isAuthorized}
              handleSetEmailAddress={handleSetEmailAddress}
              handleEmailSubmit={handleEmailSubmit}
              handleRequesResetPassword={handleRequesResetPassword}
              validateEmailAddress={validateEmailAddress}
            />
          ) : (
            <LoginModal
              hub={hub}
              action={action}
              loading={loading}
              emailAddress={emailAddress}
              password={password}
              isAuthorized={isAuthorized}
              badEmailError={badEmailError}
              emptyEmailError={emptyEmailError}
              showResetPassword={showResetPassword}
              handleSetEmailAddress={handleSetEmailAddress}
              handleSignIn={handleSignIn}
              resetAction={resetAction}
              hideRequestResetPassword={hideRequestResetPassword}
              handleEmailSubmit={handleEmailSubmit}
              handlePasswordSubmit={handlePasswordSubmit}
            />
          )}
        </Modal>
        <FooterLinks
          appStyles={hub.theme}
          links={hub.footer_links}
          // updatePadding={setPadding}
        />
      </ModalContainer>
      <Placeholder
        isOrg={false}
        isTintVisible={true}
        tintStyles={tintStyles}
        withCoverLogo={!logoUrl}
        coverLogoUrl={logoUrl}
        backgroundUrl={backgroundUrl}
      />
    </Container>
  )
}

export default HubSignIn

const Container = styled.main`
  height: 100vh;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.grayLighter};
`

const ShadowOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.1);
  z-index: 5;
`
export const Warning = styled.div`
  border: 1px solid #d0021b;
  background-color: #ffebee;
  color: #d0021b;
  display: flex;
  font-size: ${({ theme }) => theme.fontSizes.fontSizeSmaller};
  border-radius: 3px;
  padding: 12px;
  text-align: left;
  span {
    margin-left: 10px;
  }

  margin-top: 20px;
  margin-bottom: 20px;
`
export const SignInFormButton = styled(Button)`
  display: block;
  font-family: 'roboto'; //replace with a font from backend
  font-size: ${({ theme }) => theme.fontSizes.fontSizeSmaller};
  font-weight: bold;
  width: 100%;
  height: 36px;
  line-height: 36px;
  padding: 0 ${({ theme }) => theme.sizes.gutterDefault};
  margin: 15px 0;
  letter-spacing: 0.2px;
  border-radius: 2px;
  color: white;
`

export const HelperText = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.fontSizeNormal};
  margin-top: 30px;
  margin-bottom: 35px;
  color: ${({ theme }) => theme.colors.pressly};
`
const ModalContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  z-index: auto;
`

export const ModalLoginContainer = styled.div`
  display: table;
  top: 0;
  left: 0;
  table-layout: fixed;
  width: 100%;
  height: 100%;
  position: fixed;
  z-index: 10000;
  font-size: 1.8rem;
`
